import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { ParameterCod } from '@/constants/parameters';
import useGetAllNotification from '@/hooks/useGetAllNotification';
import AdminSearch from '@/modules/Search/components/widget/AdminSearch';
import { cx, getUserPermissions, hasPermission, setLocalStorageItem } from '@/utils';
import { Menu, MenuButton } from '@szhsin/react-menu';
import { AnimatePresence, motion } from 'framer-motion';
import Cookies from 'js-cookie';

import { TOKEN_ADMIN } from '@/utils/constants';
import useUserStore from '@/utils/store/store';
import SvgApps from '@/components/common/Icons/Apps';
import SvgComputerCircle from '@/components/common/Icons/ComputerCircle';
import SvgHandMoney from '@/components/common/Icons/HandMoney';
import SvgSearch from '@/components/common/Icons/Search';
import SvgUserBook from '@/components/common/Icons/UserBook';
import { useUserInfoByTokenLazyQuery } from '@/components/common/Providers/GraphqlProvider/generated';

import useRedirectSacForm from '../../../../views/Admin/components/Navbar/useRedirectSacForm';
import { useLayoutConfigContext } from '../../LayoutConfig/LayoutConfigContext';
import MenuUser from './MenuUser';
import { permissionsKeys } from './permissionsKeys';
import styles from './styles.module.css';

interface INewAdminHeaderProps {
  hideRightSection?: boolean;
}

const NewAdminHeader = ({ hideRightSection = false }: INewAdminHeaderProps) => {
  const userInfo = useUserStore((state) => state.user);
  const [refetch, { data }] = useUserInfoByTokenLazyQuery();
  const {
    layoutConfig: { parametersDefault },
  } = useLayoutConfigContext();

  const { save } = useUserStore();
  const { push } = useRouter();

  const { redirectSacByUser } = useRedirectSacForm();
  const token = Cookies.get(TOKEN_ADMIN);

  useEffect(() => {
    if (token) void refetch({ variables: { validateTokenInput: { token: token } } });
  }, [token, refetch]);

  useEffect(() => {
    if (data?.validateUserToken) {
      save({ user: data.validateUserToken });
      setLocalStorageItem('userauth', JSON.stringify(data.validateUserToken));
    }
  }, [data, save]);

  const { getAllNotifications } = useGetAllNotification();

  useEffect(() => {
    const userAuth = localStorage.getItem('userauth');

    if (!userAuth) return;

    getAllNotifications();
  }, [localStorage.getItem('userauth')]);

  const goToHome = () => push('/');
  const goToPortal = () => window.open(process.env.NEXT_PUBLIC_PORTAL_TRIBUTARIO, '_blank');

  const handleManualUsuario = () => {
    // Abrir el manual del usuario en una nueva pestaña
    window.open(
      'https://cs3develop.getoutline.com/s/d2259119-0e51-44a0-b32e-7f1ab0f69700',
      '_blank'
    );
  };

  const logoParam = parametersDefault?.find((x) => x.code === ParameterCod.Logo);
  const logoSrc = logoParam?.character;

  const [showSearch, setShowSearch] = useState(false);
  const [renderSearch, setRenderSearch] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showSearch) setRenderSearch(true);
    else if (renderSearch) {
      const timeout = setTimeout(() => setRenderSearch(false), 200);
      return () => clearTimeout(timeout);
    }
  }, [showSearch, renderSearch]);

  const handleHideSearch = () => setTimeout(() => setShowSearch(false), 500);
  const handleShowSearch = () => {
    setShowSearch(true);
    setTimeout(() => searchInputRef?.current?.focus(), 500);
  };

  return (
    <>
      <nav className={cx(styles.nav, 'z-20 flex bg-marine')}>
        <div className={cx(styles.container, 'px-12')}>
          <div
            className={cx(
              styles.left,
              'flex-1 justify-center gap-x-3 py-2 text-white md:justify-start'
            )}
          >
            {logoSrc && <img src={logoSrc} alt={'logo'} width={28} height={28} />}
            {parametersDefault?.find((x) => x.code == ParameterCod.Name)?.character}
          </div>
          {!hideRightSection && (
            <div className={cx(styles.right, 'w-auto')}>
              <div className={styles.actions}>
                <AnimatePresence initial={false}>
                  {renderSearch && showSearch && (
                    <motion.div
                      key="search-input"
                      initial={{ width: 48, opacity: 0 }}
                      animate={{ width: 320, opacity: 1 }}
                      exit={{ width: 48, opacity: 0 }}
                      transition={{ width: { duration: 0.3 }, opacity: { duration: 0.2 } }}
                    >
                      <AdminSearch
                        placeholder={'Realiza una búsqueda'}
                        iconColor="fill-marine"
                        className="w-full text-tundora"
                        maxStaticResults={4}
                        maxDynamicResults={4}
                        autoNavigate={true}
                        onInputBlur={handleHideSearch}
                        inputRef={searchInputRef}
                      />
                    </motion.div>
                  )}

                  {!showSearch && !renderSearch && (
                    <div
                      key="search-icon"
                      style={{ overflow: 'hidden', display: 'inline-block' }}
                      className="cursor-pointer rounded-full border-[2px] border-white p-2"
                      onClick={handleShowSearch}
                    >
                      <SvgSearch className="fill-white" width={24} height={24} />
                    </div>
                  )}

                  {!showSearch && renderSearch && (
                    <motion.div
                      key="search-input"
                      initial={{ width: 320, opacity: 1 }}
                      animate={{ width: 48, opacity: 0 }}
                      exit={{ width: 48, opacity: 0 }}
                      transition={{ width: { duration: 0.3 }, opacity: { duration: 0.2 } }}
                      style={{ overflow: 'hidden', display: 'inline-block' }}
                    >
                      <AdminSearch
                        placeholder={'Realiza una búsqueda'}
                        iconColor="fill-marine"
                        className="w-full text-tundora"
                        maxStaticResults={4}
                        maxDynamicResults={4}
                        autoNavigate={true}
                        onInputBlur={handleHideSearch}
                        inputRef={searchInputRef}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
                <div className="min-w-[250px]">
                  <MenuUser />
                </div>

                <Menu
                  gap={2}
                  menuClassName="w-0"
                  menuButton={
                    <MenuButton className=" flex flex-row items-center gap-2 rounded-[100px] transition-all hover:bg-blueDark focus:bg-blueDark">
                      <SvgApps className="cursor-pointer text-white" />
                    </MenuButton>
                  }
                >
                  <div className="relative right-[350px] top-[5px] z-20 flex min-w-[400px] animate-fade-in-down flex-col gap-4 rounded-md bg-white p-4 shadow-[0_0_6px_0_#00000040]">
                    <div className="absolute end-[15px] top-[-10px] h-0 w-0 border-x-[12px] border-b-[18px] border-x-transparent border-b-white"></div>
                    <label className="font-workSans font-semibold leading-[16.8px] text-tundora">
                      Accede a aplicaciones y servicios relacionados
                    </label>
                    <div className="flex flex-1 flex-row items-center justify-center gap-2">
                      <div
                        onClick={goToHome}
                        className="group flex flex-[0.5] cursor-pointer flex-col items-center gap-2 rounded-md px-6 py-3 transition-all hover:bg-marineLight"
                      >
                        <SvgComputerCircle className="text-marine transition ease-in-out group-hover:scale-90" />
                        <label className="text-center font-workSans text-[14px] leading-[16.8px] text-tundora group-hover:text-marine">
                          Ver Sede
                          <br />
                          Electrónica
                        </label>
                      </div>

                      <div
                        onClick={goToPortal}
                        className="group flex flex-[0.5] cursor-pointer flex-col items-center gap-2 rounded-md px-6 py-3 transition-all hover:bg-marineLight"
                      >
                        <SvgHandMoney className="text-marine transition ease-in-out group-hover:scale-90" />
                        <label className="text-center font-workSans text-[14px] leading-[16.8px] text-tundora group-hover:text-marine">
                          Portal Público
                          <br />
                          Tributario
                        </label>
                      </div>
                    </div>

                    <div className="flex flex-1 flex-row items-center justify-center gap-2 border-t border-gray-200 pt-4">
                      {hasPermission(getUserPermissions(), permissionsKeys.customerService) && (
                        <div
                          onClick={() => redirectSacByUser()}
                          className="group flex flex-[0.5] cursor-pointer flex-col items-center gap-2 rounded-md px-6 py-3 transition-all hover:bg-marineLight"
                        >
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-marine text-white transition ease-in-out group-hover:scale-90">
                            <span className="text-lg font-bold">SAC</span>
                          </div>
                          <label className="text-center font-workSans text-[14px] leading-[16.8px] text-tundora group-hover:text-marine">
                            Atención al
                            <br />
                            Cliente
                          </label>
                        </div>
                      )}

                      <div
                        onClick={handleManualUsuario}
                        className="group flex flex-[0.5] cursor-pointer flex-col items-center gap-2 rounded-md px-6 py-3 transition-all hover:bg-marineLight"
                      >
                        <SvgUserBook
                          className="text-marine transition ease-in-out group-hover:scale-90"
                          width={48}
                          height={48}
                        />
                        <label className="text-center font-workSans text-[14px] leading-[16.8px] text-tundora group-hover:text-marine">
                          Manual del
                          <br />
                          Usuario
                        </label>
                      </div>
                    </div>
                  </div>
                </Menu>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default NewAdminHeader;
